import React from "react";
import {
    FileText,
    Shield,
    MapPin,
    Image as ImageIcon,
    Package,
    CheckCircle,
    XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import ProductSelectionModal from "../../customer/ProductSelectionModal";
import LanguageSwitcher from "../ui/LanguageSwitcher";

const SignupView = ({
    signupForm,
    setSignupForm,
    handleSignup,
    loading,
    message,
    setCurrentView,
    products,
    selectedProducts,
    setSelectedProducts,
    files,
    setFiles,
    showProductSelection,
    setShowProductSelection,
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const documentItems = [
        {
            key: "commercialRegistration",
            label: t("auth.signup.documents.commercialRegistration"),
            icon: FileText,
        },
        {
            key: "taxNumber",
            label: t("auth.signup.documents.taxNumber"),
            icon: Shield,
        },
        {
            key: "nationalAddress",
            label: t("auth.signup.documents.nationalAddress"),
            icon: MapPin,
        },
        {
            key: "logo",
            label: t("auth.signup.documents.logo"),
            icon: ImageIcon,
        },
    ];

    return (
        <div className="relative min-h-screen bg-black text-white overflow-hidden">
            <video
                className="absolute inset-0 w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
            >
                <source src="./login-bg.mp4" type="video/mp4" />
            </video>

            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-purple-900/40 to-black/80" />

            <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
                <div className="w-full max-w-3xl bg-white/10 backdrop-blur-xl shadow-[0_0_50px_rgba(255,0,255,0.25)] my-6 rounded-2xl p-8 border border-white/20">
                    <div className="flex justify-end mb-4">
                        <LanguageSwitcher />
                    </div>

                    <h1 className="text-3xl font-bold text-center mb-6">
                        {t("auth.signup.title")}
                    </h1>

                    {message.text && (
                        <div
                            className={`mb-5 p-4 rounded-xl flex items-start gap-3 border text-sm ${
                                message.type === "success"
                                    ? "bg-green-500/10 border-green-400 text-green-200"
                                    : message.type === "error"
                                        ? "bg-red-500/10 border-red-400 text-red-200"
                                        : "bg-yellow-500/10 border-yellow-400 text-yellow-200"
                            }`}
                        >
                            {message.type === "success" ? (
                                <CheckCircle className="w-5 h-5" />
                            ) : message.type === "error" ? (
                                <XCircle className="w-5 h-5" />
                            ) : (
                                <Shield className="w-5 h-5" />
                            )}
                            <p>{message.text}</p>
                        </div>
                    )}

                    <div className="space-y-6">
                        <div>
                            <label className="block text-sm mb-1">
                                {t("auth.signup.managerNameLabel")}
                            </label>
                            <input
                                type="text"
                                value={signupForm.managerName}
                                onChange={(e) =>
                                    setSignupForm({ ...signupForm, managerName: e.target.value })
                                }
                                className="w-full py-3 px-4 rounded-xl bg-white/5 border border-white/20 placeholder-gray-300 text-white focus:ring-2 focus:ring-pink-500 outline-none"
                                placeholder={t("auth.signup.managerNamePlaceholder")}
                            />
                        </div>

                        <div>
                            <label className="block text-sm mb-1">
                                {t("auth.signup.phoneLabel")}
                            </label>
                            <input
                                type="tel"
                                value={signupForm.phoneNumber}
                                onChange={(e) =>
                                    setSignupForm({ ...signupForm, phoneNumber: e.target.value })
                                }
                                className="w-full py-3 px-4 rounded-xl bg-white/5 border border-white/20 placeholder-gray-300 text-white focus:ring-2 focus:ring-pink-500 outline-none"
                                placeholder={t("auth.signup.phonePlaceholder")}
                            />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm mb-1">
                                    {t("auth.signup.companyNameLabel")}
                                </label>
                                <input
                                    type="text"
                                    value={signupForm.companyName}
                                    onChange={(e) =>
                                        setSignupForm({ ...signupForm, companyName: e.target.value })
                                    }
                                    className="w-full py-3 px-4 rounded-xl bg-white/5 border border-white/20 placeholder-gray-300 text-white focus:ring-2 focus:ring-pink-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm mb-1">
                                    {t("auth.signup.companyNumberLabel")}
                                </label>
                                <input
                                    type="text"
                                    value={signupForm.companyNumber}
                                    onChange={(e) =>
                                        setSignupForm({ ...signupForm, companyNumber: e.target.value })
                                    }
                                    className="w-full py-3 px-4 rounded-xl bg-white/5 border border-white/20 placeholder-gray-300 text-white focus:ring-2 focus:ring-pink-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm mb-1">
                                    {t("auth.signup.emailLabel")}
                                </label>
                                <input
                                    type="email"
                                    value={signupForm.email}
                                    onChange={(e) =>
                                        setSignupForm({ ...signupForm, email: e.target.value })
                                    }
                                    className="w-full py-3 px-4 rounded-xl bg-white/5 border border-white/20 placeholder-gray-300 text-white focus:ring-2 focus:ring-pink-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm mb-1">
                                    {t("auth.signup.passwordLabel")}
                                </label>
                                <input
                                    type="password"
                                    value={signupForm.password}
                                    onChange={(e) =>
                                        setSignupForm({ ...signupForm, password: e.target.value })
                                    }
                                    className="w-full py-3 px-4 rounded-xl bg-white/5 border border-white/20 placeholder-gray-300 text-white focus:ring-2 focus:ring-pink-500"
                                />
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                            {documentItems.map((item) => (
                                <div key={item.key}>
                                    <label className=" text-sm mb-1 flex items-center gap-2">
                                        <item.icon className="w-4 h-4" />
                                        {item.label}
                                    </label>

                                    <input
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        onChange={(e) =>
                                            setFiles({
                                                ...files,
                                                [item.key]: e.target.files[0],
                                            })
                                        }
                                        className="w-full py-2 px-3 rounded-xl bg-white/5 border border-white/20 text-white cursor-pointer"
                                    />

                                    {files[item.key] && (
                                        <p className="text-green-300 text-xs mt-1">
                                            {t("messages.fileSelected", {
                                                name: files[item.key].name,
                                            })}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div>
                            <label className=" text-sm mb-2 flex items-center gap-2">
                                <Package className="w-4 h-4" />
                                {t("auth.signup.productsLabel")}
                            </label>

                            <button
                                type="button"
                                onClick={() => setShowProductSelection(true)}
                                className="w-full py-3 rounded-xl border border-pink-400/50 text-pink-200 bg-white/5 hover:bg-white/10 transition"
                            >
                                {selectedProducts.length > 0
                                    ? t("auth.signup.selectedProductsCount", {
                                        count: selectedProducts.length,
                                    })
                                    : t("auth.signup.selectProducts")}
                            </button>
                        </div>

                        <button
                            onClick={handleSignup}
                            disabled={loading}
                            className="w-full py-3 rounded-xl text-white font-semibold bg-gradient-to-r from-pink-500 to-red-500 hover:opacity-90 transition disabled:opacity-50"
                        >
                            {loading ? t("auth.signup.loading") : t("auth.signup.submit")}
                        </button>

                        <p className="text-center text-sm mt-3 text-gray-200">
                            {t("auth.signup.haveAccount")}{" "}
                            <button
                                className="text-pink-300 underline"
                                onClick={() => navigate("/login")}
                            >
                                {t("auth.signup.loginLink")}
                            </button>
                        </p>
                    </div>
                </div>

                {showProductSelection && (
                    <ProductSelectionModal
                        products={products}
                        onContinue={(selected) => {
                            setSelectedProducts(selected);
                            setShowProductSelection(false);
                        }}
                        onClose={() => setShowProductSelection(false)}
                    />
                )}
            </div>
        </div>
    );
};

export default SignupView;
